import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'

import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { ReportEditorStore } from 'modules/reports/lib/Store'
import dashboards from 'modules/reports/store/collections/dashboards'
import filters from 'modules/reports/store/collections/filters'
import projections from 'modules/reports/store/collections/projections'
import reports from 'modules/reports/store/collections/reports'
import type Report from 'modules/reports/store/models/Report'

interface Props {
  report: Report
  onClose: () => void
}

const MoveDashboardModal = ({ report, onClose }: Props) => {
  const { navigation } = useNavigation()

  const navigate = useNavigate()

  const store = React.useRef(
    new ReportEditorStore({
      report,
    })
  ).current
  React.useEffect(() => {
    store.start()
  }, [store])

  const options = React.useMemo(
    () =>
      dashboards.models.map((dashboard) => ({
        label: dashboard.attributes.title,
        value: dashboard.id,
      })),
    []
  )

  const { running: isProcessing, performAction: onSubmit } = useAsyncAction(
    async () => {
      const { success, value } = await asyncAction(
        () => reports.rpc('change', store.serializeForSave()),
        {
          successFlash: true,
          errorFlash: false,
        }
      )

      if (success) {
        store.saved = true
        await Promise.all([projections.fetch(), filters.fetch(), reports.fetch()])
        navigate(
          navigation.reports.dashboards.dashboardId.list.fullPath({
            dashboard_id: value.dashboard_id,
          })
        )
      }
    },
    {
      successFlash: false,
      errorFlash: false,
    }
  )

  return (
    <Modal
      illustration="documents"
      title={i18n.t('custom_reports.dashboard.move_dashboard.title')}
      description={i18n.t('custom_reports.dashboard.move_dashboard.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={isProcessing} submit>
            {i18n.t('common.move')}
          </Button>
        </>
      }
    >
      <Select
        none={i18n.t('custom_reports.editor.select_dashboard')}
        label={i18n.t('custom_reports.editor.dashboard')}
        field={store.reportForm.get('dashboard_id')}
        options={options}
      />
    </Modal>
  )
}

export default MoveDashboardModal
